import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getColors, fonts, formatDate as formatDateUtil } from '../theme';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { getQuoteWithSalt } from '../config/quotes';
import { getDisplayStreak } from '../utils/streakUtils';
import { HabitsSkeleton, TasksSkeleton, GoalsSkeleton, QuoteSkeleton } from '../components/LoadingSkeleton';
import { calculateGoalConsistency } from '../utils/goalConsistencyUtils';
import { Section, Row, InkBox, Bar, InkNote, QuickAddRow } from '../components/Notebook';
import AgentChat from '../agent/AgentChat';
import { useQuickAddInterpreter } from '../agent/quickAdd';
import OneScreenToday from './today/OneScreenToday';
import { getTodayLayout } from '../utils/todayLayout';
import { withoutEmoji } from '../utils/text';
import { computeProductivityScore } from '../utils/productivityScore';

const TIME_OF_DAY = { morning: 'AM', afternoon: 'Midday', evening: 'PM', night: 'Night', anytime: '—' };

// Caps a long list at `rows` ruled rows and scrolls inside, so 18 habits don't push the page down.
const ROW_PX = 49;
const ScrollList = ({ count, rows, noun, colors, children }) => {
  const capped = count > rows;
  return (
    <>
      <div
        className={capped ? 'overflow-y-auto overscroll-contain' : ''}
        style={capped ? { maxHeight: rows * ROW_PX, borderBottom: `1px solid ${colors.textPrimary}` } : undefined}
      >
        {children}
      </div>
      {capped && (
        <p className="font-mono text-[11px] pt-1.5 text-right" style={{ color: colors.textMuted }}>
          {count} {noun} · scroll for more
        </p>
      )}
    </>
  );
};

// Productivity dial: one ring, each area's contribution drawn clockwise from the top in its
// own ink tone, with square ends and hairline gaps between segments. `compact` is the
// small header version: no ticks or label, since the score title sits beside it.
const DIAL = { size: 180, stroke: 10 };
// `medium`: Today's chat header, where the score is the one figure on show:
// larger than compact, with its label under the number
const ScoreDial = ({ value, label, segments, colors, compact = false, medium = false }) => {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const { size, stroke } = DIAL;
  const c = size / 2;
  const r = c - stroke / 2 - 9;
  const circumference = 2 * Math.PI * r;
  const ticks = Array.from({ length: 20 }, (_, i) => {
    const quarter = i % 5 === 0;
    const a = (i / 20) * 2 * Math.PI;
    const r1 = r + stroke / 2 + 3;
    const r2 = r1 + (quarter ? 6 : 3);
    return { quarter, x1: c + r1 * Math.cos(a), y1: c + r1 * Math.sin(a), x2: c + r2 * Math.cos(a), y2: c + r2 * Math.sin(a) };
  });

  let start = 0;
  return (
    <div className={`relative flex-shrink-0 ${medium ? 'w-[92px] h-[92px] sm:w-[100px] sm:h-[100px]' : compact ? 'w-[64px] h-[64px] sm:w-[76px] sm:h-[76px]' : 'w-[140px] h-[140px] sm:w-[180px] sm:h-[180px]'}`}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full" style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        {!compact && !medium && ticks.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke={t.quarter ? colors.borderStrong : colors.border} strokeWidth="1" />
        ))}
        <circle cx={c} cy={c} r={r} fill="none" stroke={colors.border} strokeWidth={stroke} />
        {segments.map(seg => {
          const len = (Math.max(0, Math.min(100, seg.value)) / 100) * circumference;
          const visible = drawn ? Math.max(0, len - (len > 6 ? 2 : 0)) : 0;
          const offset = -start;
          start += len;
          return (
            <circle
              key={seg.label}
              cx={c}
              cy={c}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeLinecap="butt"
              strokeDasharray={`${visible} ${circumference}`}
              strokeDashoffset={drawn ? offset : 0}
              style={{ transition: 'stroke-dasharray .7s cubic-bezier(.2,.7,.2,1), stroke-dashoffset .7s cubic-bezier(.2,.7,.2,1)' }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div
            className={`font-mono font-light tabular-nums leading-none ${medium ? 'text-[28px] sm:text-[30px]' : compact ? 'text-[20px] sm:text-[24px]' : 'text-[44px] sm:text-[56px]'}`}
            style={{ color: colors.textPrimary, letterSpacing: '-0.05em' }}
          >
            {value}
          </div>
          {!compact && <div className={`font-mono ${medium ? 'text-[10px] mt-1' : 'text-[11px] mt-1.5'}`} style={{ color: colors.textSecondary }}>{label}</div>}
        </div>
      </div>
    </div>
  );
};

// The day's name as the title, underlined in ink that grows as today's habits get done: a faint
// full-width stroke underneath, and the ink drawn over it up to the share completed.
// When the full name doesn't fit beside the score, it becomes a two-line lockup,
// "Wednes" over "day", each sized so both lines are exactly the same width, in the height of two
// ordinary lines.
const DayTitle = ({ day, progress, label, colors }) => {
  const rootRef = useRef(null);
  const titleRef = useRef(null);
  const fullRef = useRef(null);
  const stemRef = useRef(null);
  const dayRef = useRef(null);
  const [w, setW] = useState(0);
  const [stack, setStack] = useState(null); // null: one line; else { stem, day } font sizes in px

  const splittable = day.endsWith('day') && day.length > 3;
  const stem = splittable ? day.slice(0, -3) : day;

  useEffect(() => {
    const root = rootRef.current;
    const row = root?.parentElement;
    if (!root || !row) return undefined;

    const layout = () => {
      if (!splittable || !fullRef.current) return setStack(null);
      const base = parseFloat(getComputedStyle(fullRef.current).fontSize);
      const fullW = fullRef.current.offsetWidth;
      const rowStyle = getComputedStyle(row);
      // Room for the title: the whole row when it wraps (narrow screens); otherwise what's left
      // beside the other items in the row, minus the gaps
      let available = row.clientWidth;
      if (rowStyle.flexWrap === 'nowrap') {
        const others = [...row.children].filter(el => el !== root);
        const taken = others.reduce((sum, el) => {
          const cs = getComputedStyle(el);
          return sum + (cs.flexGrow !== '0' ? parseFloat(cs.minWidth) || 0 : el.offsetWidth);
        }, 0);
        available -= taken + (parseFloat(rowStyle.columnGap) || 0) * others.length;
      }
      if (fullW <= available) return setStack(null);

      // Two lines of equal width in the height of two lines at the base size
      const a = stemRef.current.offsetWidth / base; // width per px of font size
      const b = dayRef.current.offsetWidth / base;
      let stemSize = (2 * base) / (1 + a / b);
      const shrink = Math.min(1, available / (stemSize * a));
      stemSize *= shrink;
      setStack({ stem: stemSize, day: (stemSize * a) / b });
    };

    layout();
    const observer = new ResizeObserver(layout);
    observer.observe(row);
    // Widths change once Geist arrives; re-fit whenever a font finishes loading
    document.fonts?.ready?.then(layout);
    document.fonts?.addEventListener?.('loadingdone', layout);
    return () => {
      observer.disconnect();
      document.fonts?.removeEventListener?.('loadingdone', layout);
    };
  }, [day, splittable]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;
    const measure = () => setW(el.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const titleClass = 'text-[44px] sm:text-[64px] font-semibold leading-[.95] whitespace-nowrap';
  const titleStyle = { color: colors.textPrimary, letterSpacing: '-0.045em' };
  // A hand-drawn stroke across the word, in pixels so its weight doesn't stretch
  const d = w ? `M2 7 C ${w * 0.2} 2, ${w * 0.39} 9, ${w * 0.575} 5 S ${w * 0.87} 3, ${w - 2} 6` : '';

  return (
    <div ref={rootRef} className="relative min-w-0 flex-shrink-0" role="img" aria-label={`${day}. ${label}`}>
      {/* Off-screen copies at the base size, measured to decide the layout */}
      <span aria-hidden="true" className="absolute invisible pointer-events-none left-0 top-0">
        <span ref={fullRef} className={`inline-block ${titleClass}`} style={titleStyle}>{day}</span>
        <span ref={stemRef} className={`inline-block ${titleClass}`} style={titleStyle}>{stem}</span>
        <span ref={dayRef} className={`inline-block ${titleClass}`} style={titleStyle}>day</span>
      </span>

      <h1 ref={titleRef} className={`inline-block ${titleClass}`} style={titleStyle} aria-hidden="true">
        {stack ? (
          <>
            {/* letter-spacing is set per line: inherited em spacing would stay at the parent's pixel size */}
            <span className="block" style={{ fontSize: stack.stem, letterSpacing: titleStyle.letterSpacing }}>{stem}</span>
            <span className="block" style={{ fontSize: stack.day, letterSpacing: titleStyle.letterSpacing }}>day</span>
          </>
        ) : day}
      </h1>
      {w > 0 && (
        <svg width={w} height="12" className="block mt-1.5 overflow-visible" aria-hidden="true">
          <path d={d} fill="none" stroke={colors.border} strokeWidth="1.5" strokeLinecap="round" />
          <path
            d={d}
            pathLength="1"
            fill="none"
            stroke={colors.textPrimary}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="1"
            strokeDashoffset={1 - Math.max(0, Math.min(1, progress))}
            style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(0.2, 0.7, 0.2, 1)' }}
          />
        </svg>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const {
    isDark,
    user,
    habits,
    completions,
    tasks,
    archivedTasks,
    goals,
    events,
    toggleCompletion,
    archiveTask,
    addTask,
    googleCalendarConnected,
    stats,
    dataLoading
  } = useApp();
  const { events: googleCalendarEvents } = useGoogleCalendar();
  const { hasFeature, canAddMore } = useSubscription();
  const colors = getColors(isDark);
  const navigate = useNavigate();
  // Today's add row: tasks are due today unless the text says otherwise
  const interpretTodayTask = useQuickAddInterpreter('task', { dueDate: stats.todayString });
  // One screen or the long page: a per-device choice in Settings > Appearance
  const [todayLayout] = useState(getTodayLayout);

  const hasAnalyticsAccess = hasFeature('analytics');

  // Tasks keep their row long enough for the strike-through to finish before archiving
  const [animatingOutTasks, setAnimatingOutTasks] = useState(new Set());

  const [showAllComplete, setShowAllComplete] = useState(false);
  const prevUncompletedCount = useRef(null);

  // Quote refresh state - persisted to localStorage
  const QUOTE_SALT_KEY = 'mind_quote_salt';
  const [quoteSalt, setQuoteSalt] = useState(() => {
    return parseInt(localStorage.getItem(QUOTE_SALT_KEY) || '0', 10);
  });
  const [quoteAnimating, setQuoteAnimating] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const hasTypedInitial = useRef(false);
  
  // Typewriter effect on initial load
  useEffect(() => {
    if (hasTypedInitial.current) return;
    hasTypedInitial.current = true;
    
    const quote = getQuoteWithSalt(user?.uid || '', quoteSalt);
    setDisplayedText('');
    setIsTyping(true);
    
    let i = 0;
    const typeInterval = setInterval(() => {
      if (i < quote.text.length) {
        setDisplayedText(quote.text.slice(0, i + 1));
        i++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, 45);
    
    return () => clearInterval(typeInterval);
  }, [user?.uid, quoteSalt]);

  // Combine local events with Google Calendar events, filtering out synced task/goal events
  const allEvents = useMemo(() => {
    // Get all googleEventIds from tasks and goals to filter them out from calendar events
    const syncedEventIds = new Set([
      ...(tasks || []).filter(t => t.googleEventId).map(t => t.googleEventId),
      ...(goals || []).filter(g => g.googleEventId).map(g => g.googleEventId)
    ]);
    
    // Filter Google Calendar events to exclude those that were synced from tasks/goals
    const filteredGoogleEvents = googleCalendarEvents.filter(event => {
      // If this event's ID matches a synced task/goal, exclude it
      return !syncedEventIds.has(event.id);
    });
    
    return [...(events || []), ...filteredGoogleEvents];
  }, [events, googleCalendarEvents, tasks, goals]);

  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = new Date();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const firstName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'there';
  const greetingText = `${getGreeting()}, ${firstName}`;

  const { habitProgress, overdueCount, todayString } = stats;

  const todaysHabits = habits.map(habit => ({
    ...habit,
    completed: completions[habit.id]?.[todayString] || false
  }));
  const completedToday = todaysHabits.filter(h => h.completed).length;
  const totalHabits = habits.length;
  const actualUncompleted = todaysHabits.filter(h => !h.completed).length;

  // Detect when all habits are completed and trigger celebration
  useEffect(() => {
    // Only trigger if we have habits and just went from some uncompleted to all complete
    if (totalHabits > 0 && 
        prevUncompletedCount.current !== null && 
        prevUncompletedCount.current > 0 && 
        actualUncompleted === 0) {
      // Show the ink note once the last habit has animated out
      setTimeout(() => {
        setShowAllComplete(true);
      }, 1600);
    }
    
    // Reset showAllComplete if habits become uncompleted again
    if (actualUncompleted > 0) {
      setShowAllComplete(false);
    }
    
    prevUncompletedCount.current = actualUncompleted;
  }, [actualUncompleted, totalHabits]);

  // Productivity Score calculation (Pro feature), shared with the assistant
  const productivityScore = useMemo(() => {
    if (!hasAnalyticsAccess) return { score: 0, habitScore: 0, taskScore: 0, goalScore: 0, weights: { habits: 0, tasks: 0, goals: 0 } };
    return computeProductivityScore({ habits, completions, tasks, archivedTasks, goals, overdueCount });
  }, [habits, completions, tasks, archivedTasks, goals, overdueCount, hasAnalyticsAccess]);

  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    last7Days.push(formatDateUtil(d));
  }

  const handleHabitToggle = (habitId) => {
    toggleCompletion(habitId, todayString);
  };

  const handleTaskComplete = (taskId) => {
    setAnimatingOutTasks(prev => new Set([...prev, taskId]));
    setTimeout(() => {
      archiveTask(taskId);
      setAnimatingOutTasks(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }, 480);
  };

  const tasksDueToday = stats.tasksDueToday || [];
  const overdueTasks = stats.overdueTasks || [];
  const activeDueTasks = [...overdueTasks, ...tasksDueToday].filter(
    t => !t.completed || animatingOutTasks.has(t.id)
  );

  const activeGoals = goals.filter(g => !g.completed).map(goal => {
    const totalMilestones = goal.milestones?.length || 0;
    const completedMilestones = goal.milestones?.filter(m => m.completed).length || 0;
    const progress = totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : 0;
    return { ...goal, progress, totalMilestones, completedMilestones };
  });
  const shownGoals = [...activeGoals]
    .sort((a, b) => (a.targetDate ? new Date(a.targetDate) : Infinity) - (b.targetDate ? new Date(b.targetDate) : Infinity))
    .slice(0, 4);

  const formatShortDate = (dateStr) => new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const duration = (start, end) => {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) return '';
    if (mins < 60) return `${mins}m`;
    return mins % 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins / 60}h`;
  };

  // ----- Calendar -----
  const [calMonth, setCalMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());

  const sortByTime = (a, b) => {
    if (!a.startTime && !b.startTime) return 0;
    if (!a.startTime) return -1;
    if (!b.startTime) return 1;
    return a.startTime.localeCompare(b.startTime);
  };
  const itemsForDate = (dateStr) => ({
    events: allEvents.filter(e => e.date === dateStr).sort(sortByTime),
    tasks: tasks.filter(t => t.dueDate === dateStr && !t.completed),
    goals: goals.filter(g => !g.completed && (
      g.targetDate === dateStr || g.milestones?.some(m => m.dueDate === dateStr && !m.completed)
    )),
    allHabitsDone: habits.length > 0 && habits.every(h => completions[h.id]?.[dateStr]),
  });

  const monthCells = useMemo(() => {
    const y = calMonth.getFullYear();
    const m = calMonth.getMonth();
    const startDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const count = Math.ceil((startDay + daysInMonth) / 7) * 7;
    return Array.from({ length: count }, (_, i) => new Date(y, m, 1 - startDay + i));
  }, [calMonth]);

  const shiftMonth = (delta) => setCalMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  const jumpToToday = () => {
    const now = new Date();
    setCalMonth(now);
    setSelectedDate(now);
  };

  const selectedStr = formatDateString(selectedDate);
  const selectedIsToday = selectedStr === formatDateString(today);
  const selectedItems = itemsForDate(selectedStr);
  const selectedCount = selectedItems.events.length + selectedItems.tasks.length + selectedItems.goals.length;

  // ----- Header status: what's left, most pressing first -----
  const habitsLeft = Math.max(0, totalHabits - completedToday);
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const dayStatus = (() => {
    const parts = [];
    if (habitsLeft > 0) parts.push(`${plural(habitsLeft, 'habit')} to go`);
    if (overdueTasks.length > 0) parts.push(`${overdueTasks.length} overdue`);
    else if (habitsLeft === 0 && activeDueTasks.length > 0) parts.push(`${plural(activeDueTasks.length, 'task')} left today`);
    if (parts.length) return parts.join(' · ');
    return totalHabits > 0 || tasks.length > 0 ? 'All done.' : null;
  })();
  const habitProgressToday = totalHabits ? completedToday / totalHabits : 0;

  // ----- Score breakdown -----
  const bigNumber = hasAnalyticsAccess ? productivityScore.score : habitProgress;
  const goalsAverage = activeGoals.length
    ? Math.round(activeGoals.reduce((sum, g) => sum + g.progress, 0) / activeGoals.length)
    : 0;
  const tones = [colors.textPrimary, colors.textSecondary, colors.borderStrong];
  const breakdown = hasAnalyticsAccess
    ? [
        { label: 'Habits', weight: productivityScore.weights.habits, value: productivityScore.habitScore, to: '/habits', tone: tones[0] },
        { label: 'Tasks', weight: productivityScore.weights.tasks, value: productivityScore.taskScore, to: '/tasks', tone: tones[1] },
        { label: 'Goals', weight: productivityScore.weights.goals, value: productivityScore.goalScore, to: '/goals', tone: tones[2] },
      ]
    : [
        { label: 'Habits today', value: habitProgress, to: '/habits', tone: tones[0] },
        { label: 'Goal progress', value: goalsAverage, to: '/goals' },
      ];
  // Each area fills the share of the ring it adds to the score: its score times its weight
  const dialSegments = hasAnalyticsAccess
    ? breakdown.map(row => ({ label: row.label, color: row.tone, value: (row.value * row.weight) / 100 }))
    : [{ label: 'Habits today', color: tones[0], value: habitProgress }];

  const refreshQuote = () => {
    if (isTyping) return;
    setQuoteAnimating(true);
    setTimeout(() => {
      const newSalt = Math.floor(Math.random() * 1000000);
      localStorage.setItem(QUOTE_SALT_KEY, newSalt.toString());
      setQuoteSalt(newSalt);
      setQuoteAnimating(false);
      const newQuote = getQuoteWithSalt(user?.uid || '', newSalt);
      setDisplayedText('');
      setIsTyping(true);
      let i = 0;
      const typeInterval = setInterval(() => {
        if (i < newQuote.text.length) {
          setDisplayedText(newQuote.text.slice(0, i + 1));
          i++;
        } else {
          clearInterval(typeInterval);
          setIsTyping(false);
        }
      }, 45);
    }, 300);
  };

  const mutedLink = 'hover:underline underline-offset-4';
  const ink = colors.textPrimary;
  const rule = `1px solid ${colors.border}`;
  const stepBtn = 'font-mono text-xs px-2.5 py-1 transition-colors notebook-row-hover';

  const scoreTitle = (
    <>
      <p className="text-[15px] font-semibold tracking-tight" style={{ color: ink }}>
        {hasAnalyticsAccess ? 'Productivity score' : "Today's habits"}
      </p>
      <p className="font-mono text-xs mt-0.5" style={{ color: colors.textSecondary }}>
        {hasAnalyticsAccess ? '7-day average' : '% done today'}
      </p>
      {hasAnalyticsAccess ? (
        <Link to="/how-scoring-works" className={`font-mono text-xs ${mutedLink}`} style={{ color: colors.textSecondary }}>How it's scored</Link>
      ) : (
        <Link to="/upgrade" className={`font-mono text-xs ${mutedLink}`} style={{ color: colors.textSecondary }}>Productivity score is a Pro feature</Link>
      )}
    </>
  );

  if (todayLayout === 'screen') {
    // What the score is made of, in one line: the weakest area, and overdue tasks
    const weakest = hasAnalyticsAccess ? [...breakdown].sort((a, b) => a.value - b.value)[0] : null;
    const scoreLine = hasAnalyticsAccess
      ? `${bigNumber} · ${weakest.label.toLowerCase()} lowest at ${weakest.value}%${overdueTasks.length ? ` · ${overdueTasks.length} overdue` : ''}`
      : `${bigNumber}% of today's habits done`;

    return (
      <div className="px-4" style={{ paddingTop: Capacitor.isNativePlatform() ? 8 : 72 }}>
        <OneScreenToday
          colors={colors}
          today={today}
          greetingText={greetingText}
          score={{
            dial: <ScoreDial medium value={bigNumber} label={hasAnalyticsAccess ? 'score' : 'today'} segments={dialSegments} colors={colors} />,
            // What's holding it back, beside the ring that already shows the number
            line: hasAnalyticsAccess
              ? `${weakest.label} lowest at ${weakest.value}%${overdueTasks.length ? ` · ${overdueTasks.length} overdue` : ''}`
              : "Today's habits done",
            aria: `${hasAnalyticsAccess ? 'Productivity score' : "Today's habits"} ${bigNumber}. ${scoreLine}`,
            to: hasAnalyticsAccess ? '/how-scoring-works' : '/upgrade',
            label: hasAnalyticsAccess ? 'score' : 'today',
            value: bigNumber,
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 px-4 pt-14" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-6xl 2xl:max-w-[1600px] mx-auto">
        {/* Header on the same grid as the page below: the title at the left and the score at the right
            end of the calendar column, the breakdown over the lists column. */}
        <header className="pt-6 pb-6 sm:pt-8" style={{ borderBottom: `1px solid ${ink}` }}>
          <p className="font-mono text-xs mb-2" style={{ color: colors.textSecondary }}>
            {today.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} · {greetingText}
            {!dataLoading && dayStatus && <span style={{ color: ink }}> · {dayStatus}</span>}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] 2xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-12 gap-y-6 items-end">
            <div className="2xl:col-span-2 flex flex-wrap xl:flex-nowrap items-end gap-x-8 gap-y-5 min-w-0">
              <DayTitle
                day={today.toLocaleDateString('en-US', { weekday: 'long' })}
                progress={dataLoading ? 0 : habitProgressToday}
                label={totalHabits ? `${completedToday} of ${totalHabits} habits done today` : 'No habits yet'}
                colors={colors}
              />

              {/* Score at the far end of the title's column, just before the breakdown */}
              {!dataLoading && (
                <div className="flex items-center gap-4 flex-shrink-0 lg:ml-auto">
                  <div
                    role="img"
                    aria-label={hasAnalyticsAccess
                      ? `Productivity score ${bigNumber}. ${breakdown.map(r => `${r.label} ${r.value}%, weight ${r.weight}%`).join('; ')}`
                      : `${bigNumber}% of today's habits done`}
                  >
                    <ScoreDial compact value={bigNumber} label={hasAnalyticsAccess ? 'score' : 'today'} segments={dialSegments} colors={colors} />
                  </div>
                  <div className="min-w-0">{scoreTitle}</div>
                </div>
              )}
            </div>

            {!dataLoading && (
              <div className="grid gap-3 min-w-0">
                {breakdown.map(row => (
                  <Link key={row.label} to={row.to} className="block group min-w-0">
                    <span className="flex justify-between gap-2 text-[13px] mb-1" style={{ color: ink }}>
                      <span className="truncate group-hover:underline underline-offset-4">
                        {row.tone && hasAnalyticsAccess && (
                          <span aria-hidden="true" className="inline-block w-2 h-2 mr-2 align-[1px]" style={{ backgroundColor: row.tone }} />
                        )}
                        {row.label}
                        {row.weight !== undefined && (
                          <span className="font-mono text-xs ml-2" style={{ color: colors.textSecondary }}>weight {row.weight}%</span>
                        )}
                      </span>
                      <span className="font-mono text-xs tabular-nums">{row.value}%</span>
                    </span>
                    <Bar value={row.value} max={9999} color={hasAnalyticsAccess ? row.tone : undefined} />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </header>

        <AgentChat />

        {/* Phones: lists, calendar, day. lg: calendar over day | lists. 2xl: calendar | day | lists */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] lg:grid-rows-[auto_1fr] 2xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] 2xl:grid-rows-1 gap-x-12">
          {/* Today's lists at their natural height; long ones scroll inside their own cap */}
          <div className="min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 2xl:col-start-3 2xl:row-span-1">
            <Section spacing="mt-6" title="Habits" aside={`${completedToday} / ${totalHabits}`}>
              {dataLoading ? (
                <div className="py-3"><HabitsSkeleton count={3} /></div>
              ) : totalHabits === 0 ? (
                <Row margin="" mark={null} meta={<Link to="/habits" className={`font-mono text-xs ${mutedLink}`}>Add one</Link>}>
                  <span style={{ color: colors.textSecondary }}>No habits yet.</span>
                </Row>
              ) : (
                <ScrollList count={totalHabits} rows={7} noun="habits" colors={colors}>
                {todaysHabits.map(habit => {
                  const displayStreak = getDisplayStreak(habit);
                  return (
                    <Row
                      key={habit.id}
                      onClick={() => handleHabitToggle(habit.id)}
                      margin={TIME_OF_DAY[habit.timeOfDay] || '—'}
                      mark={<InkBox checked={habit.completed} />}
                      title={habit.completed ? `Mark ${habit.name} not done` : `Mark ${habit.name} done`}
                      meta={
                        <span className="font-mono text-xs min-w-[32px] text-right" title={`Current streak: ${displayStreak} days`}>
                          {displayStreak}d
                        </span>
                      }
                    >
                      <span aria-pressed={habit.completed}>{habit.name}</span>
                    </Row>
                  );
                })}
                </ScrollList>
              )}
              {showAllComplete && totalHabits > 0 && (
                <div className="pl-[60px] sm:pl-[78px]">
                  <InkNote>Every habit, done.</InkNote>
                </div>
              )}
            </Section>

            <Section
              title="Tasks"
              aside={<Link to="/tasks" className={mutedLink}>{activeDueTasks.length} due · all</Link>}
            >
              {!dataLoading && (
                <QuickAddRow
                  margin="Today"
                  mark={<span className="font-mono text-[17px]">•</span>}
                  placeholder={activeDueTasks.length === 0 ? 'Nothing due. Add a task for today…' : 'Add a task for today…'}
                  hint="* for priority · ↵ to add"
                  interpret={interpretTodayTask}
                  onAdd={(text) => {
                    const priority = text.startsWith('*');
                    const title = priority ? text.replace(/^\*+\s*/, '') : text;
                    if (title) addTask({ title, description: '', dueDate: todayString, category: 'personal', weight: priority ? 'high' : 'medium' });
                  }}
                  blocked={!canAddMore('maxTasks', (tasks || []).length)}
                  onBlocked={() => navigate('/upgrade')}
                />
              )}
              {dataLoading ? (
                <div className="py-3"><TasksSkeleton count={2} /></div>
              ) : activeDueTasks.length === 0 ? null : (
                <ScrollList count={activeDueTasks.length} rows={7} noun="tasks" colors={colors}>
                {activeDueTasks.map(task => {
                  const done = animatingOutTasks.has(task.id);
                  const overdue = task.dueDate && task.dueDate < todayString;
                  const important = task.weight === 'high' || task.weight === 'critical';
                  return (
                    <Row
                      key={task.id}
                      onClick={() => !done && handleTaskComplete(task.id)}
                      margin={overdue ? formatShortDate(task.dueDate) : 'Today'}
                      marginStrong={overdue}
                      title={`Complete: ${task.title}`}
                      mark={<span className="font-mono text-[17px]" style={{ color: done ? colors.textSecondary : ink }}>{done ? '×' : '•'}</span>}
                      meta={overdue ? <span className="font-mono text-[11px] underline underline-offset-[3px]">overdue</span> : null}
                    >
                      {important && <span className="font-mono mr-1.5" title="Priority">*</span>}
                      <span className={`ink-strike ${done ? 'on' : ''}`} style={{ color: done ? colors.textSecondary : ink }}>
                        {task.title}
                      </span>
                    </Row>
                  );
                })}
                </ScrollList>
              )}
            </Section>

            <Section title="Goals" aside={<Link to="/goals" className={mutedLink}>{activeGoals.length} active</Link>}>
              {dataLoading ? (
                <div className="py-3"><GoalsSkeleton count={2} /></div>
              ) : shownGoals.length === 0 ? (
                <Row margin="" mark={<span className="font-mono">–</span>} meta={<Link to="/goals" className={`font-mono text-xs ${mutedLink}`}>Set one</Link>}>
                  <span style={{ color: colors.textSecondary }}>No active goals.</span>
                </Row>
              ) : (
                <div>
                {shownGoals.map(goal => {
                  const consistency = goal.linkedHabitIds?.length ? calculateGoalConsistency(completions, goal.linkedHabitIds, 7) : null;
                  return (
                    <Row
                      key={goal.id}
                      onClick={() => navigate('/goals')}
                      margin={goal.targetDate ? formatShortDate(goal.targetDate) : '—'}
                      mark={<span className="font-mono">–</span>}
                      meta={<span className="font-mono text-xs">{goal.progress}%</span>}
                    >
                      <span className="grid gap-2">
                        <span>{goal.title}</span>
                        <Bar value={goal.progress} />
                        {consistency !== null && (
                          <span className="font-mono text-xs" style={{ color: colors.textSecondary }}>
                            linked habits {consistency}% this week
                          </span>
                        )}
                      </span>
                    </Row>
                  );
                })}
                </div>
              )}
            </Section>
          </div>

          {/* Month calendar */}
          <div className="min-w-0 lg:col-start-1 lg:row-start-1">
            <Section
              spacing="mt-8 lg:mt-6"
              title={calMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              aside={
                <span className="inline-flex" style={{ border: `1px solid ${colors.borderStrong}` }}>
                  <button onClick={() => shiftMonth(-1)} className={stepBtn} aria-label="Previous month" style={{ color: ink }}>‹</button>
                  <button onClick={jumpToToday} className={stepBtn} style={{ color: ink, borderLeft: `1px solid ${colors.borderStrong}`, borderRight: `1px solid ${colors.borderStrong}` }}>Today</button>
                  <button onClick={() => shiftMonth(1)} className={stepBtn} aria-label="Next month" style={{ color: ink }}>›</button>
                </span>
              }
            >
              <div className="grid grid-cols-7 font-mono text-[11px] py-2" style={{ color: colors.textSecondary }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <span key={d} className="px-1.5 sm:px-2"><span className="sm:hidden">{d[0]}</span><span className="hidden sm:inline">{d}</span></span>
                ))}
              </div>
              <div className="grid grid-cols-7" style={{ borderTop: rule, borderLeft: rule }}>
                {monthCells.map(date => {
                  const ds = formatDateString(date);
                  const inMonth = date.getMonth() === calMonth.getMonth();
                  const isToday = ds === todayString;
                  const isSelected = ds === selectedStr;
                  const items = itemsForDate(ds);
                  return (
                    <button
                      key={ds}
                      onClick={() => setSelectedDate(date)}
                      aria-pressed={isSelected}
                      aria-label={`${date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}: ${items.events.length} events, ${items.tasks.length} tasks${items.allHabitsDone ? ', all habits done' : ''}`}
                      className="relative text-left p-1.5 sm:p-2 min-h-[52px] sm:min-h-[76px] flex flex-col justify-between gap-1 notebook-row-hover"
                      style={{
                        borderRight: rule,
                        borderBottom: rule,
                        outline: isSelected ? `1px solid ${ink}` : 'none',
                        outlineOffset: -1,
                      }}
                    >
                      <span
                        className="font-mono text-xs tabular-nums w-[22px] text-center leading-[20px]"
                        style={{
                          backgroundColor: isToday ? colors.accent : 'transparent',
                          color: isToday ? colors.onAccent : ink,
                          opacity: inMonth ? 1 : 0.4,
                        }}
                      >
                        {date.getDate()}
                      </span>
                      <span className="flex flex-wrap items-center gap-[3px]" aria-hidden="true">
                        {items.events.slice(0, 3).map((e, i) => (
                          <span key={i} className="w-1.5 h-1.5" style={{ backgroundColor: ink, opacity: inMonth ? 1 : 0.4 }} />
                        ))}
                        {items.tasks.length > 0 && (
                          <span className="w-1.5 h-1.5" style={{ border: `1px solid ${ink}`, opacity: inMonth ? 1 : 0.4 }} />
                        )}
                        {items.goals.length > 0 && (
                          <span className="w-2 h-[1.5px]" style={{ backgroundColor: ink, opacity: inMonth ? 1 : 0.4 }} />
                        )}
                      </span>
                      {items.allHabitsDone && (
                        <span aria-hidden="true" className="absolute left-0 right-0 bottom-0 h-[2px]" style={{ backgroundColor: ink }} />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] pt-2.5" style={{ color: colors.textSecondary }}>
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5" style={{ backgroundColor: ink }} />event</span>
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5" style={{ border: `1px solid ${ink}` }} />task</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-[1.5px]" style={{ backgroundColor: ink }} />goal</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-[2px]" style={{ backgroundColor: ink }} />all habits done</span>
              </div>
            </Section>
          </div>

          {/* The selected day: under the calendar on lg, its own column on 2xl */}
          <div className="min-w-0 lg:col-start-1 lg:row-start-2 2xl:col-start-2 2xl:row-start-1">
            <Section
              spacing="mt-8 2xl:mt-6"
              title={selectedIsToday ? 'Today' : selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              aside={
                <Link to={`/events?date=${selectedStr}&view=day`} className={mutedLink}>
                  {selectedCount} item{selectedCount === 1 ? '' : 's'}{googleCalendarConnected ? ' · Google Calendar' : ''} · open day
                </Link>
              }
            >
              {selectedCount === 0 ? (
                <Row margin="" mark={<span className="font-mono" style={{ color: colors.textSecondary }}>○</span>}>
                  <span style={{ color: colors.textSecondary }}>Nothing planned.</span>
                </Row>
              ) : (
                <>
                  {selectedItems.events.map((event, i) => (
                    <Row
                      key={event.id || `${event.name}-${i}`}
                      onClick={() => navigate(`/events?date=${selectedStr}&view=day`)}
                      margin={event.startTime || 'All day'}
                      mark={<span className="font-mono">○</span>}
                      meta={<span className="font-mono text-xs" style={{ color: colors.textSecondary }}>{duration(event.startTime, event.endTime)}</span>}
                    >
                      {event.name || event.title}
                    </Row>
                  ))}
                  {selectedItems.tasks.map(task => (
                    <Row key={task.id} onClick={() => navigate('/tasks')} margin="task" mark={<span className="font-mono">•</span>}>
                      {task.title}
                    </Row>
                  ))}
                  {selectedItems.goals.map(goal => (
                    <Row key={goal.id} onClick={() => navigate('/goals')} margin="goal" mark={<span className="font-mono">–</span>}>
                      {goal.title}
                    </Row>
                  ))}
                </>
              )}
            </Section>
          </div>
        </div>

        {/* Quote */}
        <section className="mt-10 pt-5" style={{ borderTop: rule }}>
          {dataLoading ? (
            <QuoteSkeleton />
          ) : (() => {
            const quote = getQuoteWithSalt(user?.uid || '', quoteSalt);
            const textToShow = isTyping ? displayedText : quote.text;
            return (
              <div className={`transition-opacity duration-150 ${quoteAnimating ? 'opacity-0' : 'opacity-100'}`}>
                <p className="text-lg italic leading-relaxed min-h-[3em] max-w-2xl" style={{ fontFamily: fonts.serif, color: ink }}>
                  “{textToShow}”
                </p>
                <div className="flex items-center justify-between gap-4 mt-2 max-w-2xl">
                  <span className="font-mono text-xs" style={{ color: colors.textSecondary }}>{quote.author}</span>
                  <button
                    onClick={refreshQuote}
                    disabled={isTyping}
                    className={`font-mono text-xs ${isTyping ? 'opacity-50 cursor-not-allowed' : mutedLink}`}
                    style={{ color: colors.textSecondary }}
                  >
                    New quote
                  </button>
                </div>
              </div>
            );
          })()}
          {/* The native tab bar has no Settings tab, so this is its way in on iOS */}
          {Capacitor.isNativePlatform() && (
            <button
              onClick={() => navigate('/settings')}
              className="mt-8 w-full py-3 text-sm"
              style={{ border: `1px solid ${ink}`, color: ink }}
            >
              Settings
            </button>
          )}
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
